import { Injectable, inject } from '@angular/core';
import { Firestore, addDoc, collection, collectionData, deleteField, doc, getDoc, getDocs, updateDoc, query, where } from '@angular/fire/firestore';
import { Observable } from 'rxjs';
import {
  SupportTicket,
  TICKET_PLATFORMS,
  TicketAttachment,
  TicketAwaitingReply,
  TicketPlatformMap,
  TicketStatus,
  TicketUpdate,
  normalizeTicketStatus,
  ticketAffectedUserIds,
  ticketParticipantIds,
} from '../models';
import { LocalAttachment } from './camera.service';
import { HelperService } from './helper.service';
import { StorageService } from './storage.service';

export interface TicketThreadActivity {
  lastMessage?: string;
  lastMessageAt?: string;
  lastMessageBy?: string;
  lastMessageByName?: string;
  awaitingReply?: TicketAwaitingReply | null;
  status?: TicketStatus;
  updatedBy?: string;
}

/**
 * Ticket CRUD lives here so SupportService (auth / dashboard) never initializes Firestore.
 */
@Injectable({
  providedIn: 'root'
})
export class SupportTicketService {

  private firestore = inject(Firestore);

  constructor(
    private helperService: HelperService,
    private storageService: StorageService,
  ) { }

  getTicketList(): Observable<SupportTicket[]> {
    return collectionData(collection(this.firestore, 'supportTickets'), { idField: 'id' }) as Observable<SupportTicket[]>;
  }

  getUserTicketList(userId: string): Observable<SupportTicket[]> {
    const tickets = query(
      collection(this.firestore, 'supportTickets'),
      where('participantIds', 'array-contains', userId),
    );
    return collectionData(tickets, { idField: 'id' }) as Observable<SupportTicket[]>;
  }

  async getTicket(ticketId: string): Promise<SupportTicket | null> {
    const ticketRef = doc(this.firestore, 'supportTickets', ticketId);
    const snap = await getDoc(ticketRef);
    if (!snap.exists()) {
      return null;
    }
    const updatesSnap = await getDocs(collection(this.firestore, `supportTickets/${ticketId}/updates`));
    const updates = {};
    updatesSnap.forEach((updateSnap) => {
      updates[updateSnap.id] = updateSnap.data();
    });
    return new SupportTicket({ ...snap.data(), id: snap.id, updates });
  }

  async createTicket(ticket: Partial<SupportTicket>): Promise<string> {
    const now = new Date().toISOString();
    const affected = this.normalizeAffected(ticket);
    const data = this.helperService.cleanObjectForFirebase({
      ...ticket,
      ...affected,
      type: ticket.type || 'bug',
      status: normalizeTicketStatus(ticket.status || 'new'),
      platforms: this.truePlatforms(ticket.platforms),
      participantIds: ticketParticipantIds({ ...ticket, ...affected }),
      creationDate: ticket.creationDate || now,
      updatedAt: now,
    });
    delete data.updates;
    // ticketNumber is assigned by Cloud Functions on create — never from the client
    delete data.ticketNumber;
    const result = await addDoc(collection(this.firestore, 'supportTickets'), data);
    return result.id;
  }

  async saveTicket(ticketId: string, patch: Partial<SupportTicket>) {
    const data: any = { updatedAt: new Date().toISOString() };
    if (patch.title !== undefined) {
      data.title = patch.title;
    }
    if (patch.description !== undefined) {
      data.description = patch.description;
    }
    if (patch.status !== undefined) {
      data.status = normalizeTicketStatus(patch.status);
    }
    if (patch.workflowStage !== undefined) {
      data.workflowStage = patch.workflowStage || deleteField();
    }
    if (patch.severity !== undefined) {
      data.severity = patch.severity;
    }
    if (patch.type !== undefined) {
      data.type = patch.type;
    }
    if (patch.assigneeId !== undefined) {
      data.assigneeId = patch.assigneeId || deleteField();
    }
    if (patch.reportedById !== undefined) {
      data.reportedById = patch.reportedById || deleteField();
    }
    if (
      patch.affectedUserIds !== undefined
      || patch.affectedUserId !== undefined
      || patch.userId !== undefined
      || patch.reportedById !== undefined
    ) {
      const affected = this.normalizeAffected(patch);
      data.affectedUserIds = affected.affectedUserIds;
      data.affectedUserId = affected.affectedUserId || deleteField();
      data.userId = affected.userId || deleteField();
      data.participantIds = ticketParticipantIds({
        reportedById: patch.reportedById,
        ...affected,
      });
    }
    if (patch.reporterLocked !== undefined) {
      data.reporterLocked = !!patch.reporterLocked;
    }
    if (patch.updatedBy) {
      data.updatedBy = patch.updatedBy;
    }
    if (patch.lastMessage !== undefined) {
      data.lastMessage = patch.lastMessage;
    }
    if (patch.lastMessageAt !== undefined) {
      data.lastMessageAt = patch.lastMessageAt;
    }
    if (patch.lastMessageBy !== undefined) {
      data.lastMessageBy = patch.lastMessageBy;
    }
    if (patch.lastMessageByName !== undefined) {
      data.lastMessageByName = patch.lastMessageByName;
    }
    if (patch.awaitingReply !== undefined) {
      data.awaitingReply = patch.awaitingReply || deleteField();
    }
    if (patch.platforms) {
      data.platforms = this.truePlatforms(patch.platforms);
    }
    if (patch.attachments !== undefined) {
      data.attachments = patch.attachments || [];
    }

    await updateDoc(doc(this.firestore, 'supportTickets', ticketId), data);
  }

  async uploadTicketAttachment(ticketId: string, attachment: LocalAttachment): Promise<TicketAttachment> {
    const safeName = (attachment.name || `${Date.now()}`).replace(/[^\w.\-]+/g, '_');
    const storagePath = `supportTickets/${ticketId}/${Date.now()}-${safeName}`;
    let url: string;

    if (attachment.file) {
      url = await this.storageService.fbPutFile(storagePath, attachment.file);
    } else if (attachment.nativePath) {
      url = await this.storageService.fpPutNativeFile(storagePath, attachment.nativePath);
    }

    if (!url) {
      throw new Error('Failed to upload ticket attachment');
    }

    const type = attachment.type === 'video' ? 'video' : 'image';
    return {
      url,
      fileName: attachment.name || safeName,
      type,
    };
  }

  async addTicketUpdate(ticketId: string, update: TicketUpdate, activity?: TicketThreadActivity) {
    const data = this.helperService.cleanObjectForFirebase(update);
    await addDoc(collection(this.firestore, `supportTickets/${ticketId}/updates`), data);
    if (!activity) {
      return;
    }
    await this.saveTicket(ticketId, activity);
  }

  /** Always write array + legacy primary fields so old tickets and new multi-affected stay compatible. */
  private normalizeAffected(ticket: Partial<SupportTicket>): {
    affectedUserIds: string[];
    affectedUserId: string;
    userId: string;
  } {
    const affectedUserIds = ticketAffectedUserIds(ticket);
    const primary = affectedUserIds[0] || '';
    return {
      affectedUserIds,
      affectedUserId: primary,
      userId: primary,
    };
  }

  private truePlatforms(platforms: TicketPlatformMap | undefined): TicketPlatformMap {
    const next: TicketPlatformMap = {};
    for (const platform of TICKET_PLATFORMS) {
      if (platforms?.[platform]) {
        next[platform] = true;
      }
    }
    return next;
  }
}
